
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { showSuccess, showError } from '@/hooks/use-toast';
import { TaxInvoiceFormData, GeneratedInvoice, SELLER_INFO } from '../types/taxInvoice';
import { secureStorage } from '@/utils/secureStorage';
import { useLanguage } from '@/contexts/LanguageContext';

interface UseTaxInvoiceProps {
  campaignId: string;
  companyDetails: {
    companyName: string;
    brandName: string;
    personInCharge: string;
    email: string;
    mobile: string;
  };
  totalAmount: number;
  paymentMethod: 'creditcard' | 'bank_transfer';
}

export const useTaxInvoice = ({ 
  campaignId, 
  companyDetails, 
  totalAmount,
  paymentMethod 
}: UseTaxInvoiceProps) => {
  const { language } = useLanguage();
  const [showTaxInvoicePreview, setShowTaxInvoicePreview] = useState(false);
  const [taxInvoiceFormData, setTaxInvoiceFormData] = useState<TaxInvoiceFormData | null>(null);
  const [generatedInvoice, setGeneratedInvoice] = useState<GeneratedInvoice | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Calculate VAT (15%)
  const subtotal = totalAmount / 1.15;
  const vatAmount = totalAmount - subtotal;

  const generateInvoiceNumber = (): string => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const seq = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${year}${month}${day}-${seq}`;
  };

  const handleTaxInvoiceFormSubmit = async (formData: TaxInvoiceFormData) => {
    setIsGenerating(true);
    setTaxInvoiceFormData(formData);

    try {
      const invoiceNumber = generateInvoiceNumber();
      const invoiceDate = new Date().toISOString();

      // Get selected locations from storage
      const selectedLocations = secureStorage.get<any[]>('selectedLocations') || 
        JSON.parse(localStorage.getItem('selectedLocations') || '[]');

      // Get duration
      const duration = parseInt(localStorage.getItem('duration') || '1', 10);

      // Build location items
      const locationItems = selectedLocations.map((loc: any) => ({
        name: loc.name || loc.locationName || 'Screen Location',
        days: duration,
        pricePerDay: (loc.dailyRate || loc.price || (subtotal / selectedLocations.length / duration)),
        total: loc.totalPrice || (subtotal / selectedLocations.length)
      }));

      // Call edge function to generate PDF
      const { data, error } = await supabase.functions.invoke('generate-tax-invoice', {
        body: {
          invoiceNumber,
          invoiceDate,
          seller: SELLER_INFO,
          buyer: {
            companyName: companyDetails.companyName,
            address: formData.buyerAddress,
            city: formData.buyerCity,
            postalCode: formData.buyerPostalCode,
            vatNumber: formData.buyerVatNumber,
            crNumber: formData.buyerCrNumber
          },
          items: locationItems,
          subtotal: Math.round(subtotal * 100) / 100,
          vatAmount: Math.round(vatAmount * 100) / 100,
          totalAmount: Math.round(totalAmount * 100) / 100,
          amountPaid: paymentMethod === 'creditcard' ? totalAmount : 0,
          remainingBalance: paymentMethod === 'creditcard' ? 0 : totalAmount,
          paymentMethod,
          language
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.pdfBase64) {
        throw new Error('No PDF generated');
      }

      setGeneratedInvoice({
        invoiceNumber,
        pdfBase64: data.pdfBase64
      });

      setShowTaxInvoicePreview(true);

      showSuccess({
        title: 'Invoice Generated',
        description: `Invoice ${invoiceNumber} has been created successfully.`
      });

    } catch (error: any) {
      console.error('Error generating tax invoice:', error);
      showError({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate tax invoice. Please try again.'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTaxInvoiceConfirm = async (): Promise<boolean> => {
    if (!generatedInvoice || !taxInvoiceFormData) {
      showError({
        title: 'Error',
        description: 'Invoice data is missing. Please try again.'
      });
      return false;
    }

    setIsProcessing(true);

    try {
      const effectiveCampaignId = campaignId || localStorage.getItem('currentCampaignId');

      // Upload PDF to storage
      const pdfBlob = base64ToBlob(generatedInvoice.pdfBase64, 'application/pdf');
      const pdfPath = `invoices/${effectiveCampaignId}/${generatedInvoice.invoiceNumber}.pdf`;

      const { error: uploadError } = await supabase.storage
        .from('purchase-order-pdfs')
        .upload(pdfPath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true
        });

      if (uploadError) {
        console.error('Error uploading PDF:', uploadError);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('purchase-order-pdfs')
        .getPublicUrl(pdfPath);

      // Only save to database if we have a valid campaign ID (UUID format)
      const isValidUuid = effectiveCampaignId?.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);

      if (isValidUuid) {
        // Save invoice record to database
        const { error: dbError } = await supabase
          .from('campaign_invoices')
          .insert({
            campaign_id: effectiveCampaignId,
            invoice_number: generatedInvoice.invoiceNumber,
            invoice_date: new Date().toISOString(),
            buyer_company_name: companyDetails.companyName,
            buyer_vat_number: taxInvoiceFormData.buyerVatNumber,
            buyer_address: taxInvoiceFormData.buyerAddress,
            buyer_city: taxInvoiceFormData.buyerCity,
            buyer_postal_code: taxInvoiceFormData.buyerPostalCode,
            buyer_cr_number: taxInvoiceFormData.buyerCrNumber || null,
            subtotal: Math.round(subtotal * 100) / 100,
            vat_amount: Math.round(vatAmount * 100) / 100,
            total_amount: Math.round(totalAmount * 100) / 100,
            pdf_url: urlData?.publicUrl || null,
            status: 'issued',
            payment_method: paymentMethod
          });

        if (dbError) {
          console.error('Error saving invoice to database:', dbError);
          // Don't throw - allow payment to proceed even if invoice save fails
        }
      }

      // Store invoice data in localStorage for reference
      localStorage.setItem('generatedInvoice', JSON.stringify({
        invoiceNumber: generatedInvoice.invoiceNumber,
        pdfUrl: urlData?.publicUrl,
        timestamp: new Date().toISOString()
      }));

      setShowTaxInvoicePreview(false);
      return true;

    } catch (error: any) {
      console.error('Error confirming tax invoice:', error);
      showError({
        title: 'Error',
        description: error.message || 'Failed to save invoice. Please try again.'
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTaxInvoiceBack = () => {
    setShowTaxInvoicePreview(false);
    // Form visibility is controlled by usePaymentOptions, just hide preview
  };

  return {
    showTaxInvoicePreview,
    taxInvoiceFormData,
    generatedInvoice,
    isGenerating,
    isProcessing,
    subtotal,
    vatAmount,
    handleTaxInvoiceFormSubmit,
    handleTaxInvoiceConfirm,
    handleTaxInvoiceBack
  };
};

// Helper function to convert base64 to Blob
function base64ToBlob(base64: string, type: string): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type });
}
